import { useEffect, useRef, useState } from 'react';
import { APIProvider, Map as MapaGoogle, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { MAPS_KEY, MAPS_ID } from '../../config';

/* Lima, para cuando la dirección todavía no tiene punto. */
const CENTRO = { lat: -12.0806, lng: -77.0282 };

/** Buscador de direcciones de Google, sobre el mapa. */
const Buscador = ({ alElegir }) => {
  const places = useMapsLibrary('places');
  const input = useRef(null);
  const [listo, setListo] = useState(false);

  useEffect(() => {
    if (!places || !input.current || listo) return;
    const auto = new places.Autocomplete(input.current, {
      /* Perú y solo direcciones: buscar "Javier Prado" no puede devolver una
         calle de Bogotá, que es lo que pasa sin esto. */
      componentRestrictions: { country: 'pe' },
      fields: ['geometry', 'formatted_address', 'address_components', 'name'],
    });
    auto.addListener('place_changed', () => {
      const p = auto.getPlace();
      if (!p?.geometry?.location) return;
      const distrito = (p.address_components || [])
        .find((c) => c.types.includes('administrative_area_level_3')
          || c.types.includes('sublocality_level_1'))?.long_name;
      alElegir({
        lat: p.geometry.location.lat(),
        lng: p.geometry.location.lng(),
        direccion: p.formatted_address || p.name,
        distrito,
      });
    });
    setListo(true);
  }, [places, listo, alElegir]);

  return (
    <input ref={input} className="fiInput" placeholder="Buscar la dirección en el mapa…"
      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} />
  );
};

/** Centra el mapa cuando el punto cambia desde fuera (una búsqueda). */
const Seguir = ({ punto }) => {
  const map = useMap();
  useEffect(() => {
    if (map && punto) map.panTo(punto);
  }, [map, punto]);
  return null;
};

/**
 * Elegir un punto en el mapa.
 *
 * Antes la única forma de corregir una dirección desde el panel era escribir
 * la latitud y la longitud a mano. Nadie tiene esos números: hay que sacarlos
 * de Google, copiarlos y pegarlos sin equivocarse en un signo.
 */
const MapaPunto = ({ lat, lng, alMover, alto = 260 }) => {
  const punto = (lat != null && lng != null && lat !== '' && lng !== '')
    ? { lat: Number(lat), lng: Number(lng) }
    : null;

  return (
    <APIProvider apiKey={MAPS_KEY} libraries={['marker', 'places']}>
      <Box sx={{ display: 'grid', gap: 1 }}>
        <Buscador alElegir={(r) => alMover(r)} />

        <Box sx={{ height: alto, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <MapaGoogle
            mapId={MAPS_ID}
            defaultCenter={punto || CENTRO}
            defaultZoom={punto ? 17 : 12}
            gestureHandling="greedy"
            disableDefaultUI
            zoomControl
            /* Tocar el mapa también mueve el pin: es lo que hace todo el mundo
               antes de descubrir que se puede arrastrar. */
            onClick={(e) => {
              const c = e.detail?.latLng;
              if (c) alMover({ lat: c.lat, lng: c.lng });
            }}
          >
            <Seguir punto={punto} />
            {punto && (
              <AdvancedMarker
                position={punto}
                draggable
                onDragEnd={(e) => {
                  const c = e.latLng;
                  if (c) alMover({ lat: c.lat(), lng: c.lng() });
                }}
              />
            )}
          </MapaGoogle>
        </Box>

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {punto
            ? 'Arrastra el pin o toca el mapa para mover el punto exacto de entrega.'
            : 'Busca la dirección arriba o toca el mapa para poner el punto.'}
        </Typography>
      </Box>
    </APIProvider>
  );
};

export default MapaPunto;
